import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../i18n";

const LINKS = [["desertCall", "#desert-call"], ["moto", "#moto"], ["fourx4", "#fourx4"], ["routes", "#routes"], ["gallery", "#gallery"], ["about", "#about"], ["quote", "#quote"], ["contact", "#contact"]];

const LangSwitch = ({ large }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 font-mono ${large ? "text-sm" : "text-xs"}`} data-testid="lang-switcher">
      {["it", "fr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}-btn`}
          className={`${large ? "px-4 py-2.5" : "px-2 py-1"} uppercase tracking-widest transition-colors duration-300 ${lang === l ? "text-[#0B0A08] bg-[#D4AF37]" : "text-[#E6C594] hover:text-white"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const FloatingWhatsApp = () => {
  const { wa } = useLang();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > window.innerHeight * 0.8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.a
          href={wa} target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="floating-whatsapp-btn"
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="lg:hidden fixed z-40 right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#0B0A08] pl-4 pr-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] shadow-[0_12px_40px_-8px_rgba(212,175,55,0.6)] active:scale-95 transition-transform"
        >
          <MessageCircle size={18} /> WhatsApp
        </motion.a>
      )}
    </AnimatePresence>
  );
};

export const Header = () => {
  const { t, wa } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled || open ? "bg-[#0B0A08]/85 backdrop-blur-xl border-b border-[#D4AF37]/15" : "bg-gradient-to-b from-black/60 to-transparent"}`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
        <Logo onClick={() => setOpen(false)} />
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map(([k, h]) => (
            <a key={k} href={h} data-testid={`nav-${k}-link`} className="nav-link text-sm text-[#F7F4EE]/85 hover:text-[#F3E5AB]">{t.nav[k]}</a>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <div className="hidden sm:block"><LangSwitch /></div>
          <span className="hidden md:block"><a href={wa} target="_blank" rel="noreferrer" data-testid="header-whatsapp-btn" className="btn-gold text-xs">{t.nav.cta}</a></span>
          <button onClick={() => setOpen(!open)} className="lg:hidden text-[#F3E5AB] p-2" data-testid="mobile-menu-toggle" aria-label="menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-[#D4AF37]/15"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4 max-h-[calc(100svh-5rem)] overflow-y-auto" data-lenis-prevent>
              {LINKS.map(([k, h], i) => (
                <motion.a
                  key={k} href={h} onClick={() => setOpen(false)} data-testid={`mobile-nav-${k}-link`}
                  initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.05 }}
                  className="font-display text-[1.75rem] leading-tight text-[#F7F4EE] py-1"
                >
                  {t.nav[k]}
                </motion.a>
              ))}
              <div className="pt-4 flex flex-wrap items-center justify-between gap-4">
                <div className="sm:hidden"><LangSwitch large /></div>
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} data-testid="mobile-menu-whatsapp-btn" className="btn-gold text-xs md:hidden"><MessageCircle size={16} /> {t.nav.cta}</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
